import React, { useContext, useMemo } from "react";
import { DebugPluginPlaceholder } from "../components/debug/DebugPluginPlaceholder";
import { DebugContext } from "../components/debug/DebugContextProvider";
import { DebugPluginComponent } from "../components/debug/DebugPluginComponent";
import { getPluginData } from "./PluginDataNeeds";
import { PluginRegistryContext } from "./PluginRegistryContextProvider";
import { Placeholder, PlaceholderGroupMember, PluginEnableContext } from "./PluginRegistryTypes";
import {
  PluginPlaceholderContext,
  PluginPlaceholderContextProvider,
} from "./PluginPlaceholderContextProvider";
import { AnalyticsContext } from "../contexts/AnalyticsContextProvider";
import { PluginDataContextProvider } from "./PluginDataContextProvider";
import { PageStaticDataContext } from "../contexts/PageStaticDataContextProvider";
import { useExperimentsContext } from "../contexts/ExperimentsProvider";
import PluginErrorBoundary from "./PluginErrorBoundary";

export interface PlaceholderProps<T> {
  placeholderType: Placeholder | PlaceholderGroupMember;
  customData?: T;
  children?: React.ReactNode | React.ReactNode[];
}

const PluginPlaceholder = <T=void,>(props: PlaceholderProps<T>) => { // eslint-disable-line
  const { placeholderType, children, customData } = props;
  const { pluginRegistry, pluginOrdering, allPluginsData } = useContext(PluginRegistryContext);
  const { isDebug } = useContext(DebugContext);
  const { componentNames: parentComponentNames } = useContext(PluginPlaceholderContext);
  const { experimentImpression, decision } = useContext(AnalyticsContext);
  const { useExperiment } = useExperimentsContext();
  const pageProps = useContext(PageStaticDataContext);
  const context: PluginEnableContext = {
    experimentImpression,
    decision,
    pageProps,
  };

  const pluginComponents = pluginRegistry.getComponents(
    useExperiment,
    placeholderType,
    context,
    parentComponentNames,
    pluginOrdering[placeholderType]
  );

  if (pluginComponents.length) {
    const reactComponents = pluginComponents.map((c, i) => {
      const pluginData = getPluginData(c.name, c.dataNeeds, allPluginsData);
      const component = (
        <PluginDataContextProvider pluginData={pluginData} key={c.name}>
          <PluginErrorBoundary pluginName={c.name} contactInfo={c.pluginOwner} isDebug={isDebug}>
            <c.Component key={i} customData={customData} />
          </PluginErrorBoundary>
        </PluginDataContextProvider>
      );

      if (isDebug) {
        return (
          <DebugPluginComponent componentName={c.name} contactInfo={c.pluginOwner} key={c.name} experimentContext={c.experimentContext}>
            {component}
          </DebugPluginComponent>
        );
      } else {
        return component;
      }
    });

    const pluginPlaceholderWithContext = (
      <PluginPlaceholderContextProvider components={pluginComponents}>
        {reactComponents}
      </PluginPlaceholderContextProvider>
    );

    if (isDebug) {
      return (
        <DebugPluginPlaceholder placeholderType={placeholderType}>
          {pluginPlaceholderWithContext}
        </DebugPluginPlaceholder>
      );
    } else {
      return pluginPlaceholderWithContext;
    }
  }

  return <>{children}</>;
};

export default PluginPlaceholder;
